import type { Client, ClientHeaders } from "@medusajs/js-sdk";

import type { SalonStockerHttpTypes } from "@salonstocker/framework";

/**
 * Client for the buyer **Supplier Match** endpoints (ADR-0005): a salon's
 * per-seller status for whether a supplier recognizes their account, and the
 * self-serve actions that drive it - auto-check ({@link checkAll}), a
 * deliberate per-seller re-check ({@link retry}), and an alternate-email
 * override for Case B ({@link getSellerState}/{@link requestSellerEmail}/
 * {@link releaseSellerMatch}). {@link confirmEmail} is the confirmation-link
 * click target and is safe to call unauthenticated.
 */
export class SupplierMatch {
  private client: Client;

  constructor(client: Client) {
    this.client = client;
  }

  /** The full grid - one card per active seller, a pure local read. */
  list = async (
    headers?: ClientHeaders
  ): Promise<SalonStockerHttpTypes.StoreSupplierMatchListResponse> =>
    this.client.fetch<SalonStockerHttpTypes.StoreSupplierMatchListResponse>(
      "/store/supplier-matches",
      { method: "GET", headers }
    );

  /**
   * Auto-check every not-yet-settled, lookup-capable seller (ADR-0005
   * "Matching runs automatically"). Idempotent - safe to call on every grid
   * mount, including when everything is already settled.
   */
  checkAll = async (
    headers?: ClientHeaders
  ): Promise<SalonStockerHttpTypes.StoreSupplierMatchCheckResponse> =>
    this.client.fetch<SalonStockerHttpTypes.StoreSupplierMatchCheckResponse>(
      "/store/supplier-matches/check",
      { method: "POST", headers }
    );

  /** Force a fresh lookup for one seller, regardless of its current status. */
  retry = async (
    sellerId: string,
    headers?: ClientHeaders
  ): Promise<SalonStockerHttpTypes.StoreRetrySupplierMatchResponse> =>
    this.client.fetch<SalonStockerHttpTypes.StoreRetrySupplierMatchResponse>(
      `/store/supplier-matches/${sellerId}/retry`,
      { method: "POST", headers }
    );

  /** A seller's Case B email state - never includes the confirmation token. */
  getSellerState = async (
    sellerId: string,
    headers?: ClientHeaders
  ): Promise<SalonStockerHttpTypes.StoreSupplierMatchEmailStateResponse> =>
    this.client.fetch<SalonStockerHttpTypes.StoreSupplierMatchEmailStateResponse>(
      `/store/sellers/${sellerId}/supplier-match`,
      { method: "GET", headers }
    );

  /**
   * Request an alternate lookup email for one seller (Case B). Applies
   * immediately with no confirmation email if the address is already
   * verified elsewhere for this salon; otherwise sends the confirmation email.
   */
  requestSellerEmail = async (
    sellerId: string,
    email: string,
    headers?: ClientHeaders
  ): Promise<SalonStockerHttpTypes.StoreSupplierMatchEmailStateResponse> =>
    this.client.fetch<SalonStockerHttpTypes.StoreSupplierMatchEmailStateResponse>(
      `/store/sellers/${sellerId}/supplier-match`,
      {
        method: "POST",
        body: {
          email
        } satisfies SalonStockerHttpTypes.StoreRequestSupplierMatchEmailPayload,
        headers
      }
    );

  /**
   * Declare a trade account with a claimable seller (one with no lookup
   * integration) under `email`. Claims at once for the login email or an
   * address already verified for this salon; otherwise sends a confirmation
   * email and the card stays `pending` until the click. Rejects with a 400
   * (`not_claimable`) or 409 (`match_is_matched`, `email_in_use`). Remove a
   * claim with {@link releaseSellerMatch}.
   */
  claim = async (
    sellerId: string,
    email: string,
    headers?: ClientHeaders
  ): Promise<SalonStockerHttpTypes.StoreClaimSupplierMatchResponse> =>
    this.client.fetch<SalonStockerHttpTypes.StoreClaimSupplierMatchResponse>(
      `/store/sellers/${sellerId}/supplier-match/claim`,
      {
        method: "POST",
        body: {
          email
        } satisfies SalonStockerHttpTypes.StoreClaimSupplierMatchPayload,
        headers
      }
    );

  /** Release the salon's match or claim for one seller - reverts checkout to the account email. */
  releaseSellerMatch = async (
    sellerId: string,
    headers?: ClientHeaders
  ): Promise<SalonStockerHttpTypes.StoreSupplierMatchEmailStateResponse> =>
    this.client.fetch<SalonStockerHttpTypes.StoreSupplierMatchEmailStateResponse>(
      `/store/sellers/${sellerId}/supplier-match`,
      { method: "DELETE", headers }
    );

  /**
   * Confirm a Case B email by its confirmation token. Deliberately callable
   * unauthenticated - the thing being proven is mailbox ownership, not
   * SalonStocker-account identity (ADR-0005).
   */
  confirmEmail = async (
    token: string,
    headers?: ClientHeaders
  ): Promise<SalonStockerHttpTypes.StoreConfirmSupplierMatchEmailResponse> =>
    this.client.fetch<SalonStockerHttpTypes.StoreConfirmSupplierMatchEmailResponse>(
      "/store/supplier-match/confirm",
      {
        method: "POST",
        body: {
          token
        } satisfies SalonStockerHttpTypes.StoreConfirmSupplierMatchEmailPayload,
        headers
      }
    );
}
